import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAdminReview } from "../../../hooks/useAdminReview";
import { ReviewStatus } from "../../../types/review";
import { AdminLayout } from "../../layout";
import { Button, Card } from "../../ui";
import Icon from "../../ui/Icon";

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<"pending" | "completed">(
    (searchParams.get("tab") as "pending" | "completed") || "pending"
  );
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 10;

  const {
    reviews,
    isLoading,
    totalCount,
    totalPages,
    fetchReviews,
    refreshReviews,
  } = useAdminReview();

  // 탭 변경 시 데이터 로드
  useEffect(() => {
    const status: ReviewStatus =
      activeTab === "pending" ? "PENDING" : "APPROVED";
    fetchReviews({
      page: currentPage,
      limit,
      status,
    });
  }, [activeTab, currentPage, fetchReviews]);

  // 페이지 변경
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // 검수 페이지로 이동
  const handleReview = (messageId: string) => {
    navigate(`/admin/review/${messageId}?tab=${activeTab}`);
  };

  // 시간 포맷팅
  const formatDateTime = (date: Date | string | null | undefined) => {
    if (!date) return "-";

    try {
      const d = typeof date === "string" ? new Date(date) : date;

      if (isNaN(d.getTime())) return "-";

      return d.toLocaleString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "-";
    }
  };

  // 상태 뱃지 스타일
  const getStatusBadge = (status: ReviewStatus) => {
    switch (status) {
      case "APPROVED":
        return "bg-green-100 text-green-800";
      case "REJECTED":
        return "bg-red-100 text-red-800";
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <AdminLayout title="AI 답변 검수">
      <div className="h-full flex flex-col">
        {/* 탭 헤더 - 고정 */}
        <div className="flex-shrink-0 border-b border-gray-200 pb-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => {
                  setActiveTab("pending");
                  setCurrentPage(1);
                  navigate("/admin/dashboard?tab=pending");
                }}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  activeTab === "pending"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                검수 대기
              </button>
              <button
                onClick={() => {
                  setActiveTab("completed");
                  setCurrentPage(1);
                  navigate("/admin/dashboard?tab=completed");
                }}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  activeTab === "completed"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                검수 완료
              </button>
            </div>

            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-600">
                총 {totalCount}개 항목
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={refreshReviews}
                disabled={isLoading}
              >
                <Icon name="refresh" className="mr-2" size="sm" />
                새로고침
              </Button>
            </div>
          </div>
        </div>

        {/* 검수 목록 - 스크롤 가능 */}
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4 pb-6">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="text-gray-600">로딩 중...</span>
                </div>
              </div>
            ) : reviews.length === 0 ? (
              <Card className="p-12 text-center">
                <Icon
                  name="inbox"
                  className="mx-auto mb-4 text-gray-400"
                  size="xl"
                />
                <h3 className="text-lg font-medium text-gray-800 mb-2">
                  {activeTab === "pending"
                    ? "검수 대기 중인 항목이 없습니다"
                    : "검수 완료된 항목이 없습니다"}
                </h3>
                <p className="text-gray-600">
                  {activeTab === "pending"
                    ? "새로운 AI 답변이 들어오면 여기에 표시됩니다."
                    : "검수가 완료된 항목들이 여기에 표시됩니다."}
                </p>
              </Card>
            ) : (
              reviews.map((review) => (
                <Card key={review.id} className="p-4">
                  <div className="space-y-3">
                    {/* 헤더 - 승인 정보 (완료된 경우만) */}
                    {activeTab === "completed" && (
                      <div className="flex items-center justify-between">
                        <div></div> {/* 왼쪽 공간 */}
                        <div className="flex items-center space-x-2">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(
                              review.reviewStatus
                            )}`}
                          >
                            {review.reviewStatus === "APPROVED" && "승인됨"}
                            {review.reviewStatus === "REJECTED" && "거부됨"}
                          </span>
                          {review.reviewedByAdminName && (
                            <p className="text-xs text-gray-500">
                              by {review.reviewedByAdminName}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* 질문과 답변 */}
                    <div className="space-y-2">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">
                          사용자 질문
                        </p>
                        <p
                          className="text-gray-900 text-sm overflow-hidden"
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {review.userQuestionPreview}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-blue-700 mb-1">
                          AI 답변
                        </p>
                        <p
                          className="text-gray-900 text-sm overflow-hidden"
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {review.aiResponsePreview}
                        </p>
                      </div>
                    </div>

                    {/* 시간과 액션 */}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <p className="text-xs text-gray-500">
                        {formatDateTime(review.createdAt)}
                      </p>

                      <Button
                        size="sm"
                        onClick={() => handleReview(review.id)}
                        className={
                          activeTab === "pending"
                            ? "bg-blue-600 hover:bg-blue-700"
                            : "text-gray-600 hover:text-gray-800 border-gray-300"
                        }
                        variant={
                          activeTab === "pending" ? "primary" : "outline"
                        }
                      >
                        {activeTab === "pending" ? "검수하기" : "상세보기"}
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            )}

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div className="flex justify-center pt-4">
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      handlePageChange(Math.max(1, currentPage - 1))
                    }
                    disabled={currentPage === 1}
                  >
                    <Icon name="chevronLeft" size="sm" />
                  </Button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(
                      (page) =>
                        page === 1 ||
                        page === totalPages ||
                        Math.abs(page - currentPage) <= 2
                    )
                    .map((page) => (
                      <Button
                        key={page}
                        variant={page === currentPage ? "primary" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        className={
                          page === currentPage ? "bg-blue-600 text-white" : ""
                        }
                      >
                        {page}
                      </Button>
                    ))}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      handlePageChange(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                  >
                    <Icon name="chevronRight" size="sm" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
